// The one way the portal asks auth0-spa-js for an access token, so no request exchanges the shared
// refresh token outside the portal's token lock (AB#5012, AB#5757). The portal's lane getters
// (Auth0StatusProvider) use it, and so does the isolated Stripe return document, which shares the
// portal's Auth0 session but has no lane getters. Anything else takes its token from a lane getter
// (`getAccessToken` in api/config).
//
// A token still fresh in the shared cache needs no refresh-token exchange, so it is read without
// waiting for the token lock, but only when the read cannot write to the cache (auth/cachedAccessToken).
// Otherwise, or if the token has left the cache meanwhile, the request takes the lock
// (auth/tokenRequestLock).
import type { Auth0ContextInterface } from '@auth0/auth0-react';
import { hasFreshCachedAccessToken } from './cachedAccessToken';
import { withTokenRequestLock } from './tokenRequestLock';

type GetAccessTokenSilently = Auth0ContextInterface['getAccessTokenSilently'];

export interface AccessTokenRequest {
  clientId: string;
  // Passed to Auth0 as they are, including `organization` when the token must carry one.
  authorizationParams: Record<string, string>;
  // The organisation the token must belong to; undefined for an organisation-less account.
  organization?: string;
  // How long the request waits for the token lock, and then for Auth0.
  timeoutMs: number;
}

const readFreshCachedAccessToken = async (
  getAccessTokenSilently: GetAccessTokenSilently,
  { clientId, authorizationParams, organization }: AccessTokenRequest,
) => {
  if (!hasFreshCachedAccessToken({
    clientId,
    audience: authorizationParams.audience,
    scope: authorizationParams.scope,
    organization,
  })) {
    return undefined;
  }

  try {
    const cachedToken: unknown = await getAccessTokenSilently({ authorizationParams, cacheMode: 'cache-only' });
    return typeof cachedToken === 'string' && cachedToken ? cachedToken : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Gets an access token from the shared cache when it is fresh there, and otherwise under the token lock.
 * The caller still checks the token's organisation and audience before using it.
 */
export const requestAccessToken = async (
  getAccessTokenSilently: GetAccessTokenSilently,
  request: AccessTokenRequest,
): Promise<string> => {
  const cachedToken = await readFreshCachedAccessToken(getAccessTokenSilently, request);
  if (cachedToken) return cachedToken;

  // A request that may exchange the shared refresh token, or rewrite its cache entry, runs one at a
  // time across every lane and tab, so no two of the portal's exchanges race. The SDK operation is
  // bounded too, so a late token cannot arrive after the caller has moved on.
  return withTokenRequestLock(() => getAccessTokenSilently({
    authorizationParams: request.authorizationParams,
    timeoutInSeconds: request.timeoutMs / 1_000,
  }), request.timeoutMs);
};
